import { STALE_TIMES } from '@iheartjane/react-query-utils';
import type {
  DefinedUseQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { AlgoliaProduct } from '@jane/search/types';
import { fetchMaterializedMenuProduct } from '@jane/shared/data-access';
import type { QueryBody } from '@jane/shared/data-access';
import type { MaterializedMenuProduct } from '@jane/shared/types';

interface MaterializedMenuProductProps {
  productId: number;
  storeId: number;
}

type GetMaterializedMenuProductQueryBody = QueryBody<
  MaterializedMenuProduct | null,
  unknown,
  MaterializedMenuProduct,
  (string | number | undefined)[]
>;

type MaterializedMenuProductQueryOptions = Pick<
  GetMaterializedMenuProductQueryBody,
  'initialData'
>;

/* Query Body Overloads */
export function getMaterializedMenuProductQueryBody(
  productId: MaterializedMenuProduct['product_id'] | undefined,
  storeId: number,
  queryOptions?: Omit<MaterializedMenuProductQueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): GetMaterializedMenuProductQueryBody;

export function getMaterializedMenuProductQueryBody(
  productId: MaterializedMenuProduct['product_id'] | undefined,
  storeId: number,
  queryOptions?: Omit<MaterializedMenuProductQueryOptions, 'initialData'> & {
    initialData: MaterializedMenuProduct | (() => MaterializedMenuProduct);
  }
): GetMaterializedMenuProductQueryBody;

export function getMaterializedMenuProductQueryBody(
  productId: MaterializedMenuProduct['product_id'] | undefined,
  storeId: number,
  queryOptions: object = {}
) {
  return {
    enabled: !!storeId && !!productId,
    queryFn: () =>
      fetchMaterializedMenuProduct({ productId: Number(productId), storeId }),
    queryKey: ['materializedMenuProduct', storeId, productId],
    staleTime: STALE_TIMES.short,
    ...queryOptions,
  };
}

/* Hooks */
export function useGetMaterializedMenuProduct(
  params: MaterializedMenuProductProps,
  queryOptions?: Omit<MaterializedMenuProductQueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): UseQueryResult<MaterializedMenuProduct, unknown>;

export function useGetMaterializedMenuProduct(
  params: MaterializedMenuProductProps,
  queryOptions?: Omit<MaterializedMenuProductQueryOptions, 'initialData'> & {
    initialData:
      | MaterializedMenuProduct
      | AlgoliaProduct
      | (() => MaterializedMenuProduct)
      | (() => AlgoliaProduct);
  }
): DefinedUseQueryResult<MaterializedMenuProduct, unknown>;

export function useGetMaterializedMenuProduct(
  params: MaterializedMenuProductProps,
  queryOptions?: object
) {
  const { productId, storeId } = params;

  return useQuery(
    getMaterializedMenuProductQueryBody(productId, storeId, queryOptions)
  );
}
